import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { allpayments } from 'models/allpayments'; // Importing allpayments model
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Converts a file to a Base64 string.
 * @param {File} file - The file to convert.
 * @returns {Promise<string>} - A promise that resolves with the Base64 string.
 */
function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader(); // Create a FileReader instance
        reader.readAsDataURL(file); // Read the file as a data URL
        reader.onload = () => resolve(reader.result); // Resolve with the result on load
        reader.onerror = error => reject(error); // Reject on error
    });
}

/**
 * Class representing the crypto table view.
 * @extends JetView
 */
export default class tableCryptoView extends JetView {
    /**
     * Configures the layout of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function
        return {
            rows: [
                {
                    id: 'preloader', // ID for the preloader
                    cols: [
                        {
                            view: 'datatable', // DataTable view
                            id: 'datatable', // ID for the datatable
                            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
                            select: true, // Enable row selection
                            scrollY: true, // Enable vertical scrolling
                            columns: [
                                {
                                    id: 'rating',
                                    header: _('Acciones'),
                                    width: 100,
                                    height: 25,
                                    template: function (obj) {
                                        let content = '';

                                        if (devConfig.getPermAction('CryptosView', 'Edit')) {
                                            content += "<span class='webix_icon wxi-search view' style='margin-right:8px; cursor:pointer;'></span>";
                                        }

                                        if (devConfig.getPermAction('CryptosEdit', 'Edit')) {
                                            content += "<span class='webix_icon wxi-close edit'></span>";
                                        }

                                        // Si hay al menos un ícono, centramos el contenedor
                                        if (content) {
                                            return "<div style='display:flex; justify-content:center; align-items:center; height:100%'>" + content + "</div>";
                                        } else {
                                            return '';
                                        }
                                    }
                                },
                                {
                                    id: 'currencyId', // Column ID
                                    header: _('Id Criptomoneda'), // Localized column header
                                    width: 300, // Column width
                                    sort: 'int', // Sorting type
                                },
                                {
                                    id: 'name', // Column ID
                                    header: _('Nombre de Criptomoneda'), // Localized column header
                                    width: 450, // Column width
                                    sort: 'int', // Sorting type
                                },
                                {
                                    id: 'iso', // Column ID
                                    header: _('Código ISO'), // Localized column header
                                    width: 450, // Column width
                                    sort: 'string', // Sorting type
                                },
                                {
                                    id: 'iconImage',
                                    header: _('Icono/Imagen'),
                                    width: 100, // ancho más pequeño para imagen
                                    template: obj => obj.iconImage ? `<img src="${obj.iconImage}" style="width:50px; height:50px;" />` : '',
                                    sort: 'string',
                                    hidden: true, // Hide the icon column by default
                                },
                                {
                                    id: 'behavior', // Column ID
                                    header: _('Tipo de Criptomoneda'), // Localized column header
                                    editor: 'combo', // Combo editor for this column
                                    options: [
                                        // Options for the combo
                                        {id: '1', value: _('Estable')}, // Active option
                                        {id: '2', value: _('Inestable')}, // Inactive option
                                    ],
                                    width: 400, // Column width
                                    sort: 'int', // Sorting type
                                },
                                {
                                    id: 'status', // Column ID
                                    header: _('Estado'), // Localized column header
                                    editor: 'combo', // Combo editor for this column
                                    options: [
                                        // Options for the combo editor
                                        { id: '1', value: _('Activa') }, // Active option
                                        { id: '0', value: _('Inactiva') }, // Inactive option
                                    ],
                                    width: 400, // Column width
                                    sort: 'int', // Sorting type
                                },
                            ],
                            onClick: {
                                // Event handler for click events
                                edit: function (euv, id) {
                                    var item = this.getItem(id); // Get the clicked item
                                    $$("editform").setValues(item); // Set form values to the clicked item
                                    // Setear el valor específicamente en el template de imagen
                                    if (item.iconImage) {
                                        $$("editIconPreview").setValues({ iconImage: item.iconImage });
                                        $$("editIconPreview").show();
                                    } else {
                                        $$("editIconPreview").setValues({ iconImage: "" });
                                        $$("editIconPreview").hide();
                                    }
                                    // Edit icon click event
                                    $$('editwinCryptos').show(); // Show edit window

                                },
                                // Event handler for click events
                                view: function (euv, id) {
                                    var item = this.getItem(id); // Get the clicked item
                                    $$("viewform").setValues(item); // Set form values to the clicked item
                                    if (item.iconImage) {
                                        $$("viewIconPreview").setValues({ iconImage: item.iconImage });
                                        $$("viewIconPreview").show();
                                    } else {
                                        $$("viewIconPreview").setValues({ iconImage: "" });
                                        $$("viewIconPreview").hide();
                                    }
                                    // Edit icon click event
                                    $$('viewwinCryptos').show(); // Show edit window
                                },
                            },
                            math: true, // Enable math operations
                            loadahead: devConfig.getDefaultDataFetch(), // Load ahead configuration
                            pager: 'pagerA', // Pager ID
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and binds events.
     * @param {Object} grid - The grid object to initialize.
     */
    init(grid) {
        const _ = this.app.getService('locale')._; // Localization service
        grid = $$('datatable'); // Reference to the datatable
        var url = devConfig.geturlApi() + 'CryptoCurrency/GetCurrencies'; // API URL for fetching cryptocurrencies
        var filter = ''; // Filter string for data loading
        var popup = webix.ui({
            // Popup window for editing providers
            view: 'window',
            id: 'editwinCryptos', // ID for the edit window
            autowidth: screen.width < 500 ? 350 : 450, // Width based on screen size
            minHeight: screen.width < 500 ? 500 : 350, // Height based on screen size
            autoheight: true, // Auto height based on content
            head: {
                view: 'toolbar', // Toolbar for the window header
                cols: [
                    { view: 'label', label: _('Formulario de Edición de Criptomoneda') }, // Label for editing cryptocurrency
                    {
                        view: 'icon', // Close icon
                        icon: 'wxi-close',
                        click: function () {
                            // Close button click event
                            this.getTopParentView().hide(); // Hide the popup
                        },
                    },
                ],
            },
            position: 'center', // Center position of the window
            modal: true, // Modal window
            body: {
                view: 'scrollview', // Scrollable view
                scroll: 'y', // Vertical scrolling
                body: {
                    view: 'form', // Form for editing cryptocurrency details
                    id: 'editform', // ID for the edit form
                    width: screen.width < 500 ? 350 : 450, // Width based on screen size
                    elements: [
                        {
                            view: 'text', // Text input for ID
                            label: 'Id Criptomoneda',
                            name: 'currencyId', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'text', // Text input for Name
                            label: _('Nombre de Criptomoneda'),
                            name: 'name', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'text', // Text input for Name
                            label: _('Código ISO'),
                            name: 'iso', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Tipo de Criptomoneda'),
                            name: 'behavior', // Name for the input
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Estable') }, // Active option
                                { id: '2', value: _('Inestable') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: "template",
                            id: "editIconPreview",
                            template: function (obj) {
                                return `<div style="text-align: center;">
                                            <img src="${obj.iconImage}" style="max-height: 100px;" />
                                       </div>`;
                            },
                            height: 120,
                            borderless: true
                        },
                        {
                            view: "uploader",
                            id: "editIconUploader",
                            value: _("Subir Icono"),
                            accept: "image/png, image/jpeg",
                            name: "iconImage",
                            labelPosition: screen.width < 500 ? 'top' : 'left',
                            labelWidth: 180,
                            inputWidth: 410,
                            on: {
                                onBeforeFileAdd: function (upload) {
                                    getBase64(upload.file).then(data => {
                                        $$("editform").setValues({ iconImage: data }, true);
                                        $$("editIconUploader").define("label", upload.name);
                                        $$("editIconUploader").refresh();

                                        $$("editIconPreview").setValues({ iconImage: data });
                                        $$("editIconPreview").show();
                                    });
                                    return false;
                                }
                            }
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Estado'),
                            name: 'status', // Name for the input
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Activa') }, // Active option
                                { id: '0', value: _('Inactiva') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            cols: [
                                // Button layout
                                {
                                    view: 'button', // Cancel button
                                    value: _('Cancelar'),
                                    click: function () {
                                        $$("editIconUploader").define("label", _("Subir Icono"));
                                        $$("editIconUploader").refresh();
                                        // Cancel button click event
                                        this.getTopParentView().hide(); // Hide the popup
                                    },
                                },
                                {
                                    view: 'button', // Save button
                                    type: 'form',
                                    value: _('Guardar'),
                                    click: function ($e) {
                                        // Save button click event
                                        var values = $$('editform').getValues(); // Get form values
                                        webix
                                            .ajax() // AJAX request to save changes
                                            .headers({
                                                'Content-type': 'application/json', // Set content type
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'CryptoCurrency/UpdateCurrency', // API URL for updating cryptocurrencies
                                                '{"currencyId":"' +
                                                values.currencyId +
                                                '","status":"' +
                                                values.status +
                                                '","name":"' +
                                                values.name +
                                                '","iso":"' +
                                                values.iso +
                                                '","iconImage":"' +
                                                values.iconImage +
                                                '","behavior":"' +
                                                values.behavior +
                                                '"}', // JSON payload
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Error message on failure
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Cell value was changed', // Success message on success
                                                        );
                                                        grid.clearAll(); // Clear the grid
                                                        grid.load(
                                                            url + '?start=0&count=' + grid.data.$pagesize + filter, // Reload grid data
                                                        );
                                                    },
                                                },
                                            );
                                        $$('editform').setValues(
                                            $$('editform').getCleanValues(), // Reset form values
                                        );
                                        $$("editIconUploader").define("label", _("Subir Icono"));
                                        $$("editIconUploader").refresh();
                                        this.getTopParentView().hide(); // Hide the popup
                                    },
                                },
                            ],
                        },
                    ],
                },
            },
        });
        var popupView = webix.ui({
            // Popup window for editing providers
            view: 'window',
            id: 'viewwinCryptos', // ID for the edit window
            autowidth: screen.width < 500 ? 350 : 450, // Width based on screen size
            minHeight: screen.width < 500 ? 500 : 350, // Height based on screen size
            autoheight: true, // Auto height based on content
            head: {
                view: 'toolbar', // Toolbar for the window header
                cols: [
                    { view: 'label', label: _('Visualización de Criptomoneda') }, // Label for editing cryptocurrency
                    {
                        view: 'icon', // Close icon
                        icon: 'wxi-close',
                        click: function () {
                            // Close button click event
                            this.getTopParentView().hide(); // Hide the popup
                        },
                    },
                ],
            },
            position: 'center', // Center position of the window
            modal: true, // Modal window
            body: {
                view: 'scrollview', // Scrollable view
                scroll: 'y', // Vertical scrolling
                body: {
                    view: 'form', // Form for editing cryptocurrency details
                    id: 'viewform', // ID for the edit form
                    width: screen.width < 500 ? 350 : 450, // Width based on screen size
                    elements: [
                        {
                            view: 'text', // Text input for ID
                            label: 'Id Criptomoneda',
                            name: 'currencyId', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'text', // Text input for Name
                            label: _('Nombre de Criptomoneda'),
                            name: 'name', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'text', // Text input for Name
                            label: _('Código ISO'),
                            name: 'iso', // Name for the input
                            readonly: true, // Read-only field
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Tipo de Criptomoneda'),
                            name: 'behavior', // Name for the input
                            readonly: true, // Read-only field
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Estable') }, // Active option
                                { id: '2', value: _('Inestable') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: 'label', // Combo input for State
                            label: _('Icono'),
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            view: "template",
                            id: "viewIconPreview",
                            template: function (obj) {
                                const hasImage = obj.iconImage && obj.iconImage.trim() !== "" && obj.iconImage !== "null" && obj.iconImage !== "undefined";
                                return `<div style="text-align: center;">
                                            <img src="${obj.iconImage}" style="max-height: 100px;" />    
                                        </div>`;
                            },
                            height: 120,
                            borderless: true
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Estado'),
                            name: 'status', // Name for the input
                            readonly: true, // Read-only field
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Activa') }, // Active option
                                { id: '0', value: _('Inactiva') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                        },
                        {
                            cols: [
                                // Button layout
                                {
                                    view: 'button', // Cancel button
                                    value: _('Cerrar'),
                                    click: function () {
                                        // Cancel button click event
                                        this.getTopParentView().hide(); // Hide the popup
                                    },
                                },
                            ],
                        },
                    ],
                },
            },
        });
        var popupAdd = webix.ui({
            // Popup window for adding Cryptocurrencies
            view: 'window',
            id: 'AddCryptos', // ID for the add window
            autowidth: screen.width < 500 ? 350 : 450, // Width based on screen size
            minHeight: screen.width < 500 ? 450 : 300, // Height based on screen size
            autoheight: true, // Auto height based on content
            head: {
                view: 'toolbar', // Toolbar for the window header
                cols: [
                    { view: 'label', label: _('Formulario de Registro de Criptomoneda') }, // Label for adding cryptocurrency
                    {
                        view: 'icon', // Close icon
                        icon: 'wxi-close',
                        click: function () {
                            // Close button click event
                            this.getTopParentView().hide(); // Hide the popup
                        },
                    },
                ],
            },
            position: 'center', // Center position of the window
            modal: true, // Modal window
            body: {
                view: 'scrollview', // Scrollable view
                scroll: 'y', // Vertical scrolling
                body: {
                    view: 'form', // Form for adding cryptocurrency details
                    id: 'addform', // ID for the add form
                    width: screen.width < 500 ? 350 : 450, // Width based on screen size
                    elements: [
                        {
                            view: 'text', // Text input for Name
                            label: _('Nombre de Criptomoneda'),
                            name: 'name', // Name for the input
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                            required: true, // Required field
                        },
                        {
                            view: 'text', // Text input for Name
                            label: _('Código ISO'),
                            name: 'iso', // Name for the input
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                            required: true, // Required field
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Tipo de Criptomoneda'),
                            name: 'behavior', // Name for the input
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Estable') }, // Active option
                                { id: '2', value: _('Inestable') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                            required: true, // Required field
                        },
                        {
                            view: "template",
                            id: "addIconPreview",
                            template: function (obj) {
                                return obj.iconImage
                                    ? `<div style="text-align: center;">
                                            <img src="${obj.iconImage}" style="max-height: 100px;" />
                                       </div>` : "";
                            },
                            height: 120,
                            borderless: true
                        },
                        {
                            view: "uploader",
                            id: "iconImage",
                            value: _("Subir Icono"), // Button label
                            accept: "image/png, image/jpeg", // Limit to images
                            name: "iconImage",
                            labelPosition: screen.width < 500 ? 'top' : 'left',
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                            on: {
                                onBeforeFileAdd: function (upload) {
                                    getBase64(upload.file).then(data => {
                                        $$("addform").setValues({ iconImage: data }, true); // almacena en el form
                                        $$("iconImage").define("label", upload.name);
                                        $$("iconImage").refresh();

                                        // Actualizar previsualización
                                        $$("addIconPreview").setValues({ iconImage: data });
                                        $$("addIconPreview").show();
                                    });
                                    return false;
                                }
                            }
                        },
                        {
                            view: 'combo', // Combo input for State
                            label: _('Estado'),
                            name: 'status', // Name for the input
                            options: [
                                // Options for the combo
                                { id: '1', value: _('Activa') }, // Active option
                                { id: '0', value: _('Inactiva') }, // Inactive option
                            ],
                            labelPosition: screen.width < 500 ? 'top' : 'left', // Label position based on screen size
                            labelWidth: 180,   // space reserved for the label
                            inputWidth: 410,   // width of the input
                            required: true, // Required field
                        },
                        {
                            cols: [
                                // Button layout
                                {
                                    view: 'button', // Cancel button
                                    value: _('Cancelar'),
                                    click: function () {
                                        // Cancel button click event
                                        this.getTopParentView().hide(); // Hide the popup
                                        $$('addform').setValues(
                                            $$('addform').getCleanValues(), // Reset form values
                                        );
                                    },
                                },
                                {
                                    view: 'button', // Save button
                                    type: 'form',
                                    value: _('Guardar'),
                                    click: function ($e) {
                                        // Save button click event
                                        var values = $$('addform').getValues(); // Get form values
                                        var form = $$('addform');

                                        if (!form.validate()) {
                                            webix.message({ type: "error", text: _("Debe completar todos los campos requeridos") });
                                            return;
                                        }

                                        webix
                                            .ajax() // AJAX request to save new cryptocurrency
                                            .headers({
                                                'Content-type': 'application/json', // Set content type
                                            })
                                            .post(
                                                devConfig.geturlApi() + 'CryptoCurrency/CreateCurrency', // API URL for creating Cryptocurrencies
                                                '{"status":"' + values.status + '","name":"' + values.name + '","iconImage":"' + values.iconImage + '","iso":"' + values.iso + '","behavior":"' + values.behavior +'"}', // JSON payload
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Error! ', // Error message on failure
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        webix.message(
                                                            'Cell value was changed', // Success message on success
                                                        );
                                                        grid.clearAll(); // Clear the grid
                                                        grid.load(
                                                            url + '?start=0&count=' + grid.data.$pagesize + filter, // Reload grid data
                                                        );
                                                    },
                                                },
                                            );
                                        $$('addform').setValues(
                                            $$('addform').getCleanValues(), // Reset form values
                                        );
                                        this.getTopParentView().hide(); // Hide the popup
                                    },
                                },
                            ],
                        },
                    ],
                },
            },
        });
        this.on(this.app, 'addCryptos:button', () => {
            const form = $$("addform");

            form.clear(); // Clears form values
            form.setValues(form.getCleanValues()); // Restores default values if any

            // Reset the uploader: label and value
            const uploader = $$("iconImage");
            uploader.define("label", _("Subir Icono")); // Reverts to original text
            uploader.refresh();
            form.setValues({ iconImage: "" }, true); // Ensures base64 field is cleared

            // Hide and reset the image preview
            $$("addIconPreview").hide();
            $$("addIconPreview").setValues({ iconImage: "" });

            popupAdd.show(); // Show clean popup
        });
        this.on(this.app, 'cryptos:filter', (filterData) => {
            // Event listener for filtering cryptocurrencies
            var object = Object.entries(filterData); // Convert filter data to entries
            var string1 = ''; // Initialize filter string
            for (var i = 0; i < object.length; i++) {
                // Iterate through filter data
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    // Check for non-object and non-empty values
                    if (object[i][0] == 'Datepicker') {
                        // Handle datepicker filter
                        var date = object[i][1].split(' - '); // Split date range
                        console.log(date); // Log date range
                        string1 = string1 + '&dateFrom=' + date[0]; // Add dateFrom to filter string
                        string1 = string1 + '&dateTo=' + date[1]; // Add dateTo to filter string
                    } else {
                        string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Add other filters to string
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        // Handle object filters
                        var object2 = Object.entries(object[i][1]); // Convert object to entries
                        for (var j = 0; j < object2.length; j++) {
                            // Iterate through object entries
                            if (object2[j][1] !== null) {
                                // Check for non-null values
                                if (object2[j][0] === 'start') {
                                    // Handle start date
                                    var d = new Date(object2[j][1]); // Create date object
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2); // Format dateFrom
                                    string1 = string1 + '&' + dateFrom; // Add dateFrom to filter string
                                } else {
                                    if (object2[j][0] === 'end') {
                                        // Handle end date
                                        var d = new Date(object2[j][1]); // Create date object
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2); // Format dateTo
                                        string1 = string1 + '&' + dateTo; // Add dateTo to filter string
                                    }
                                }
                            }
                        }
                    }
                }
            }
            grid.clearAll(); // Clear the grid
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + string1); // Load filtered data
            filter = string1; // Update filter variable
        });
        grid.attachEvent('onAfterSort', function (by, dir, as) {
            // Event handler for sorting
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + filter + '&sort[' + by + ']=' + dir, // Load sorted data
            );
        });
        grid.attachEvent('onItemDblClick', function (id, e, node) {
            const item = this.getItem(id);

            // Setear los valores en el formulario completo
            $$("editform").setValues(item);

            // Setear el valor específicamente en el template de imagen
            if (item.iconImage) {
                $$("editIconPreview").setValues({ iconImage: item.iconImage });
                $$("editIconPreview").show();
            } else {
                $$("editIconPreview").setValues({ iconImage: "" });
                $$("editIconPreview").hide();
            }

            popup.show();
        });
        this.on(this.app, 'player:select', (Id) => {
            // Event listener for player selection
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + '&PlayerId=' + Id, // Load data for selected player
            );
        });
        $$('editform').bind($$('datatable')); // Bind edit form to datatable
    }
}

// FILE DOCUMENTED
